// Helpers puros do painel para o encaminhamento: serializacao do padrao global
// para a RPC, mensagens de erro amigaveis, conversao de valores em reais e
// tokenizacao do HTML do Telegram para exibir a previa sem innerHTML.

import {
  MAX_TERM_LENGTH,
  buildTemplateVars,
  extractOffer,
  renderTemplate,
  resolveTemplateBody,
  serializeWindow,
  templateRefFor,
  type ForwardingSettings,
  type UserTemplate,
  type ValueFilter,
} from "@lucralink/shared";

/** Oferta de exemplo usada nas previas (texto proprio, nao vem do easyfy). */
export const SAMPLE_OFFER_TEXT =
  "🔥 Fone Bluetooth JBL Tune 520BT\nDe R$ 299,90 por R$ 199,90\nCupom: FONE10\nhttps://s.shopee.com.br/exemplo";
export const SAMPLE_AFFILIATE_URL = "https://lucralink.com.br/r/AbC1234";

/** HTML (Telegram) da previa: template do provider/global, ou o texto original. */
export function buildPreviewHtml(
  settings: ForwardingSettings,
  templates: readonly UserTemplate[],
  providerCode: string,
  bodyOverride?: string,
): string {
  const offer = extractOffer(SAMPLE_OFFER_TEXT);
  const vars = buildTemplateVars(offer, SAMPLE_AFFILIATE_URL, providerCode);
  if (bodyOverride !== undefined) {
    return renderTemplate(bodyOverride, vars, "telegram");
  }
  if (!settings.useTemplate) {
    return renderTemplate(
      SAMPLE_OFFER_TEXT.replace("https://s.shopee.com.br/exemplo", "{affiliateUrl}"),
      vars,
      "telegram",
    );
  }
  const ref = templateRefFor(
    providerCode,
    settings.defaultTemplateRef,
    settings.templateByProvider,
  );
  return renderTemplate(resolveTemplateBody(ref, templates), vars, "telegram");
}

/** Payload jsonb aceito por save_forwarding_settings. */
export function serializeSettings(settings: ForwardingSettings): Record<string, unknown> {
  return {
    mode: settings.mode,
    window: serializeWindow(settings.window),
    use_template: settings.useTemplate,
    link_style: settings.linkStyle,
    ignored_terms: [...settings.ignoredTerms],
    custom_shorteners: [...settings.customShorteners],
    value_filter: serializeValueFilter(settings.valueFilter),
    dedup_window_minutes: settings.dedupWindowMinutes,
    default_template_ref: settings.defaultTemplateRef,
    template_by_provider: { ...settings.templateByProvider },
  };
}

export function serializeValueFilter(filter: ValueFilter): Record<string, unknown> {
  return {
    enabled: filter.enabled,
    min_price_cents: filter.minPriceCents,
    max_price_cents: filter.maxPriceCents,
    min_discount_percent: filter.minDiscountPercent,
    price_unknown_action: filter.priceUnknownAction,
  };
}

const ERROR_MESSAGES: Readonly<Record<string, string>> = {
  forwarding_destination_limit_reached:
    "Você atingiu o limite de destinos do seu plano. Desative um destino ou faça upgrade.",
  template_limit_reached: "Você já tem 10 templates. Apague um para criar outro.",
  monitored_source_limit_reached:
    "Você atingiu o limite de origens monitoradas do seu plano. Desative uma origem ou faça upgrade.",
  plan_feature_required: "Esse recurso faz parte dos planos pagos. Faça upgrade para usar.",
  invalid_ignored_terms: "Revise os termos: até 50, cada um com no máximo 100 caracteres.",
  invalid_custom_shorteners:
    "Revise os encurtadores: até 50 domínios válidos, sem caminho nem porta.",
  invalid_window: "Revise a agenda: o horário de início precisa ser antes do fim.",
  invalid_value_filter: "Revise o filtro por valor: use apenas números positivos.",
  invalid_template_ref: "O template escolhido não existe mais. Escolha outro.",
  invalid_template_name: "Dê um nome de 1 a 60 caracteres ao template.",
  invalid_template_body: "O texto do template não pode ficar vazio nem passar de 2.000 caracteres.",
  source_not_found: "Essa origem não está mais cadastrada.",
  tenant_not_found: "Não encontramos sua empresa. Entre novamente.",
  destination_ownership_required:
    "Para usar este canal como destino, adicione o @LucraLinkBot como administrador dele pela sua conta do Telegram vinculada (aba Bot nos meus grupos).",
  forwarding_queue_full: "A fila de envio está cheia. Aguarde as ofertas pendentes saírem.",
  text_templates_requires_affiliate_url:
    "O template precisa ter a variável {affiliateUrl}, senão a oferta sairia sem link.",
  forwarding_settings_provider_map_check:
    "Template por loja inválido. Escolha de novo os templates por plataforma.",
  channel_not_found: "Esse canal não está mais cadastrado.",
};

/** Traduz o erro da RPC/Postgres em texto para o usuario. */
export function forwardingErrorMessage(error: unknown): string {
  const raw =
    error instanceof Error
      ? error.message
      : typeof error === "object" && error && "message" in error
        ? String((error as { message: unknown }).message)
        : "";
  const key = Object.keys(ERROR_MESSAGES).find((k) => raw.includes(k));
  if (key) return ERROR_MESSAGES[key]!;
  if (raw.startsWith("destination_not_found"))
    return "Um dos destinos escolhidos não está mais disponível.";
  return "Não foi possível salvar. Tente de novo em instantes.";
}

/** "19,90" / "1.299,90" / "20" -> centavos. Vazio = null. Invalido = NaN. */
export function parseReais(input: string): number | null {
  const value = input.trim();
  if (value === "") return null;
  if (!/^\d{1,3}(\.\d{3})*(,\d{1,2})?$|^\d+(,\d{1,2})?$/.test(value)) return Number.NaN;
  const [int, dec = ""] = value.replace(/\./g, "").split(",");
  return Number(int) * 100 + Number(dec.padEnd(2, "0"));
}

/** Centavos -> "1.299,90" (campo de edicao, sem "R$"). */
export function formatReaisInput(cents: number | null): string {
  if (cents === null) return "";
  const reais = Math.floor(cents / 100);
  const dec = String(cents % 100).padStart(2, "0");
  return `${String(reais).replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${dec}`;
}

export type FormatTag = "b" | "i" | "s" | "u";

export interface FormattedSegment {
  readonly text: string;
  readonly tags: readonly FormatTag[];
}

const TAG_RE = /<(\/?)(b|i|s|u)>/g;

function unescapeHtml(value: string): string {
  return value.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");
}

/** Quebra o HTML gerado pelo renderTemplate (so b/i/s/u) em segmentos formatados. */
export function tokenizeTelegramHtml(html: string): FormattedSegment[] {
  const segments: FormattedSegment[] = [];
  const stack: FormatTag[] = [];
  let last = 0;
  for (const match of html.matchAll(TAG_RE)) {
    const index = match.index ?? 0;
    if (index > last)
      segments.push({ text: unescapeHtml(html.slice(last, index)), tags: [...stack] });
    const tag = match[2] as FormatTag;
    if (match[1] === "/") {
      const pos = stack.lastIndexOf(tag);
      if (pos >= 0) stack.splice(pos, 1);
    } else {
      stack.push(tag);
    }
    last = index + match[0].length;
  }
  if (last < html.length) segments.push({ text: unescapeHtml(html.slice(last)), tags: [...stack] });
  return segments;
}

/** Termo a ignorar valido (1..100 caracteres apos trim). */
export function normalizeTerm(raw: string): string | null {
  const term = raw.trim();
  return term.length === 0 || term.length > MAX_TERM_LENGTH ? null : term;
}
